import React, { useState } from 'react'
import { connect } from 'react-redux'
import { browserHistory } from 'react-router'
import styles from './FriendInteractionsButtons.scss'
import { ThreeDotCTA, CTA } from 'common/styleguide/CTA'
import { SEND_MESSAGE_DIALOG } from 'common/dialogs/SendMessage'
import { CONFIRM_UPDATE_FOLLOW_MODAL } from 'common/modals/ConfirmUpdateFollowModal'
import { GENERAL_MODAL } from 'common/modals/GeneralModal'
import { CircleSpinner } from 'common/spinners'
import { useFormatMessage, Msg, commonCTAs, commonVerbs } from 'lib/localization'
import { showModal } from 'lib/modalUtils'
import { c } from 'lib/textUtils'
import { followActions, followSelectors } from 'data/resources/follows'
import { defineMessages, injectIntl } from 'react-intl'
import { Map } from 'immutable'

export const messages = defineMessages({
  addToFriends: {
    id: 'FriendInteractionsButtons.addToFriends',
    defaultMessage: 'Add to Friends'
  },
  message: {
    id: 'FriendInteractionsButtons.message',
    defaultMessage: 'Message'
  },
  removeFriend: {
    id: 'FriendInteractionsButtons.removeFriend',
    defaultMessage: 'Remove Friend'
  },
  unfollow: {
    id: 'FriendInteractionsButtons.unfollow',
    defaultMessage: 'Unfollow',
    description: 'Verb: stop following'
  },
  following: {
    id: 'FriendInteractionsButtons.following',
    defaultMessage: 'Following'
  },
  block: {
    id: 'FriendInteractionsButtons.block',
    defaultMessage: 'Block',
    description: 'Verb: prevents a user from interacting with the user who blocked them'
  },
  blocked: {
    id: 'FriendInteractionsButtons.blocked',
    defaultMessage: 'Blocked'
  },
  unblock: {
    id: 'FriendInteractionsButtons.unblock',
    defaultMessage: 'Unblock',
    description: 'Verb: allows a user who was blocked to interact with the user who blocked them'
  },
  spam: {
    id: 'FriendInteractionsButtons.spam',
    defaultMessage: 'spam',
    description: 'Noun: when a user abuses the message system by sending many messages'
  },
  reportSpam: {
    id: 'FriendInteractionsButtons.reportSpam',
    defaultMessage: 'Report Spam',
    description: 'Verb: reports a user for spamming'
  },
  harassment: {
    id: 'FriendInteractionsButtons.harassment',
    defaultMessage: 'harassment',
    description: 'Noun: aggressive pressure or intimidation'
  },
  reportHarassment: {
    id: 'FriendInteractionsButtons.reportHarassment',
    defaultMessage: 'Report Harassment',
    description: 'Verb: reports a user for harassment'
  },
  publicWarning: {
    id: 'FriendInteractionsButtons.publicWarningV2',
    defaultMessage: `
    Note: Your profile is currently set to 'Public', which means that the user you
    have just blocked will be able to view your profile by signing out of their
    account. The only way to be sure that this user is unable to access your
    content is to set your profile to 'Friends Only' or 'Private'.
    {br}{br}
    <a1>Learn more about privacy settings</a1>
    {br}
    <a2>Change your privacy settings</a2>
    `
  }
})

const actionWithConfirm = (currentUser, user, targetLevel, onConfirm) => () => {
  showModal(CONFIRM_UPDATE_FOLLOW_MODAL, {
    currentUser,
    reportedUser: user,
    targetLevel,
    onConfirm
  })
}

export const followMenuItems = ({ inflightAction, follow, followUser, unfollowUser, updateFollow, user, currentUser, fm }) => {
  const privacyWarning = () => {
    if (currentUser.visibility === 0) {
      showModal(GENERAL_MODAL, {
        confirmText: fm(commonCTAs.ok),
        component: <div className={styles.success}>{fm(messages.publicWarning, {
          a1: (...c) => <a href='/help/privacy-settings'>{c}</a>,
          a2: (...c) => <a href='/settings/preferences'>{c}</a>,
          br: <br />
        })}</div>
      })
    }
  }

  const createOrUpdateWithPrivacyWarning = level => inflightAction(() => {
    follow ? updateFollow(follow.id, level) : followUser(user.id, level)
    privacyWarning()
  })

  const blockUser = createOrUpdateWithPrivacyWarning('block')
  const markAsSpam = createOrUpdateWithPrivacyWarning('spam')
  const markAsHarassment = createOrUpdateWithPrivacyWarning('harassment')

  let menuItems = Map()
  menuItems = ['block', 'spam', 'harassment'].includes(follow?.level)
    ? menuItems.set(fm(messages.unblock), inflightAction(() => unfollowUser(follow.id)))
    : menuItems.set(fm(messages.block), actionWithConfirm(currentUser, user, 'block', blockUser))
  menuItems = menuItems
    .set(fm(messages.reportSpam), actionWithConfirm(currentUser, user, 'spam', markAsSpam))
    .set(fm(messages.reportHarassment), actionWithConfirm(currentUser, user, 'harassment', markAsHarassment))

  return menuItems
}

const _FriendInteractionsButtons = ({
  user,
  currentUser,
  privateUser,
  follow,
  followUser,
  updateFollow,
  unfollowUser,
  intl
}) => {
  const fm = useFormatMessage()
  const [inflight, setInflight] = useState(false)
  const [hover, setHover] = useState(false)

  const loggedInUser = !!currentUser.id
  const currentUserIsUser = currentUser.id === user.id
  const currentUserIsFriendsWithUser = follow?.level === 'friend'

  const inflightAction = action => () => {
    setInflight(true)
    Promise.resolve(action()).then(() => setInflight(false))
  }

  let menuItems = followMenuItems({ inflightAction, follow, followUser, unfollowUser, updateFollow, user, currentUser, fm })

  let ctaString, ctaAction
  switch (follow?.level) {
    case 'block':
    case 'spam':
    case 'harassment':
      ctaString = fm(messages.blocked)
      ctaAction = () => {}
      break
    case 'follow':
    case 'friend':
      ctaString = fm(hover ? messages.unfollow : messages.following)
      ctaAction = actionWithConfirm(currentUser, user, 'unfollow', inflightAction(() => unfollowUser(follow.id)))
      break
    default:
      ctaString = c(fm(commonVerbs.follow))
      ctaAction = inflightAction(() => followUser(user.id))
  }
  if (inflight) ctaString = <CircleSpinner size={20} />

  return (
    <div className={styles.CTAs}>
      {!loggedInUser
        ? <>
          <CTA inline margins={false} href='/signup'><Msg capitalized m={commonVerbs.follow} /></CTA>
          {privateUser
            ? <CTA inline margins={false} link onClick={() => browserHistory.goBack()}><Msg m={commonCTAs.goBack} /></CTA>
            : <CTA className={styles.collapseCTA} inline link margins={false} href='/signup'><Msg m={messages.message} /></CTA>}
        </>
        : <>
          {!currentUserIsUser && <>
            <ThreeDotCTA
              containerClassName={styles.followCTA}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              onClick={ctaAction}
              menuItems={menuItems}>
              {ctaString}
            </ThreeDotCTA>
            {follow && ['follow', 'friend'].includes(follow.level) && <CTA
              margins={false}
              style={{ flex: '0 1 33%' }}
              link
              onClick={() => updateFollow(follow.id, currentUserIsFriendsWithUser ? 'follow' : 'friend')}>
              {fm(currentUserIsFriendsWithUser ? messages.removeFriend : messages.addToFriends)}
            </CTA>}
            {(!follow || ['follow', 'friend'].includes(follow.level)) && <CTA
              style={{ paddingLeft: 0, flex: '0 1 33%' }}
              link
              margins={false}
              onClick={() => showModal(SEND_MESSAGE_DIALOG, { toUser: user })}>
              {fm(messages.message)}
            </CTA>}
        </>}
      </>}
      {currentUserIsUser && <CTA href='/settings/account' inline margins={false}><Msg m={commonCTAs.editProfile} /></CTA>}
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  const { currentUser } = state
  const userId = ownProps.userId || (ownProps.user && ownProps.user.id)

  return {
    currentUser,
    userId,
    follow: followSelectors.byUserAndItem(state, currentUser.id, userId, 'User', false)
  }
}

const mapDispatchToProps = dispatch => ({
  followUser: (userId, level = 'follow') => dispatch(followActions.create({ itemId: userId, itemType: 'User', level })),
  updateFollow: (followId, level) => dispatch(followActions.update(followId, { follow: { level } })),
  unfollowUser: followId => dispatch(followActions.delete(followId))
})

export const FriendInteractionsButtons = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(_FriendInteractionsButtons))
